import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarBlank, Download, UserList, Receipt, Money, CurrencyDollar } from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCur = (n, cur = "MXN") => `${cur === "USD" ? "$" : "MX$"}${fmt(n)}`;
const fmtDate = (iso) => { try { return new Date(iso + "T12:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "short" }); } catch { return iso; } };

const toISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d = new Date()) => {
  const x = new Date(d); const day = x.getDay() || 7; x.setDate(x.getDate() - day + 1); x.setHours(0,0,0,0); return x;
};

const STATUS_COLOR = { draft: "#52525B", sent: "#1E88E5", paid: "#00873D", overdue: "#FFC000", canceled: "#E0000F" };
const STATUS_LABEL = { draft: "Borrador", sent: "Enviada", paid: "Pagada", overdue: "Vencida", canceled: "Cancelada" };

export default function SellerSalesReport() {
  const today = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(toISO(mondayOf()));
  const [to, setTo] = useState(toISO(today));
  const [sellerId, setSellerId] = useState("all");
  const [sellers, setSellers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/reports/sellers-list").then((r) => setSellers(r.data || [])).catch(() => setSellers([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/seller-sales", {
        params: { date_from: from, date_to: to, seller_user_id: sellerId },
      });
      setData(d);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [from, to, sellerId]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (preset) => {
    const t = new Date();
    let f, e;
    if (preset === "this_week") { f = mondayOf(t); e = t; }
    else if (preset === "last_week") { f = mondayOf(addDays(t, -7)); e = addDays(f, 6); }
    else if (preset === "this_month") { f = new Date(t.getFullYear(), t.getMonth(), 1); e = t; }
    else if (preset === "last_month") { f = new Date(t.getFullYear(), t.getMonth() - 1, 1); e = new Date(t.getFullYear(), t.getMonth(), 0); }
    setFrom(toISO(f)); setTo(toISO(e));
  };

  const exportCSV = () => {
    if (!data) return;
    const headers = ["Folio", "Fecha", "Vendedor", "Cliente", "Total", "Pagado", "Saldo", "Moneda", "Estatus"];
    const rows = data.sales.map((s) => [s.folio, s.invoice_date, s.seller_name, s.client_name, s.total, s.paid_total, s.outstanding, s.currency, s.display_status]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ventas_${sellerId}_${from}_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="border border-zinc-200 bg-white" data-testid="seller-sales-section">
      <div className="px-5 py-4 border-b border-zinc-100 flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
          <UserList size={20} weight="duotone" className="text-[#002FA7]"/> Ventas por vendedor
        </h3>
        <div className="ml-auto flex flex-wrap gap-2 items-center">
          <div className="inline-flex border border-zinc-200 rounded overflow-hidden text-xs">
            {[["this_week", "Esta semana"], ["last_week", "Semana pasada"], ["this_month", "Este mes"], ["last_month", "Mes pasado"]].map(([v, l]) => (
              <button key={v} onClick={() => applyPreset(v)} className="px-2.5 py-1.5 text-zinc-600 hover:bg-zinc-50" data-testid={`seller-preset-${v}`}>{l}</button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 border border-zinc-200 rounded px-2 py-1">
            <CalendarBlank size={14} className="text-zinc-500"/>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 px-1 border-0 text-xs w-32" data-testid="seller-date-from"/>
            <span className="text-zinc-400">→</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-7 px-1 border-0 text-xs w-32" data-testid="seller-date-to"/>
          </div>
          <select value={sellerId} onChange={(e) => setSellerId(e.target.value)} className="text-xs border border-zinc-200 rounded px-2 py-1.5 bg-white" data-testid="seller-select">
            <option value="all">Todos los vendedores</option>
            {sellers.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1" data-testid="seller-export-csv"><Download size={14}/> CSV</Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-zinc-400">Cargando…</div>
      ) : !data || data.totals.count === 0 ? (
        <div className="p-12 text-center text-zinc-400 dotted-bg">Sin ventas en el rango seleccionado.</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-zinc-50 border-b border-zinc-100">
            <Kpi icon={Receipt} label="Notas" value={data.totals.count} color="#002FA7"/>
            <Kpi icon={UserList} label="Vendedores" value={data.totals.sellers_count} color="#7E57C2"/>
            <Kpi icon={CurrencyDollar} label="Ventas brutas" value={fmtCur(data.totals.gross)} color="#00873D"/>
            <Kpi icon={Money} label="Por cobrar" value={fmtCur(data.totals.outstanding)} color="#E0000F"/>
          </div>

          {/* Per-seller summary (solo si "all") */}
          {sellerId === "all" && data.sellers.length > 1 && (
            <div className="overflow-x-auto border-b border-zinc-100">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-2">Vendedor</th>
                    <th className="text-right px-4 py-2"># Notas</th>
                    <th className="text-right px-4 py-2">Ticket promedio</th>
                    <th className="text-right px-4 py-2">Brutas</th>
                    <th className="text-right px-4 py-2">Pagado</th>
                    <th className="text-right px-4 py-2">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sellers.map((s) => (
                    <tr key={s.user_id} className="border-t border-zinc-100" data-testid={`seller-row-${s.user_id}`}>
                      <td className="px-4 py-2 font-semibold">{s.name}</td>
                      <td className="px-4 py-2 text-right numeric">{s.sales_count}</td>
                      <td className="px-4 py-2 text-right numeric text-zinc-500">{fmtCur(s.avg_ticket)}</td>
                      <td className="px-4 py-2 text-right numeric font-semibold">{fmtCur(s.gross)}</td>
                      <td className="px-4 py-2 text-right numeric text-emerald-700">{fmtCur(s.paid)}</td>
                      <td className="px-4 py-2 text-right numeric text-red-700">{fmtCur(s.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Detail of sales */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="seller-sales-table">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2">Folio</th>
                  <th className="text-left px-4 py-2">Fecha</th>
                  <th className="text-left px-4 py-2">Vendedor</th>
                  <th className="text-left px-4 py-2">Cliente</th>
                  <th className="text-center px-4 py-2">Estatus</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-right px-4 py-2">Pagado</th>
                  <th className="text-right px-4 py-2">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {data.sales.map((s) => (
                  <tr key={s.id} className="border-t border-zinc-100">
                    <td className="px-4 py-2 font-mono-data">#{String(s.folio).padStart(6, "0")}</td>
                    <td className="px-4 py-2 text-zinc-600 text-xs">{fmtDate(s.invoice_date)}</td>
                    <td className="px-4 py-2 text-zinc-700">{s.seller_name}</td>
                    <td className="px-4 py-2 truncate max-w-xs">{s.client_name}</td>
                    <td className="px-4 py-2 text-center">
                      <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-sm text-white" style={{ background: STATUS_COLOR[s.display_status] || "#52525B" }}>
                        {STATUS_LABEL[s.display_status] || s.display_status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right numeric font-semibold">{fmtCur(s.total, s.currency)}</td>
                    <td className="px-4 py-2 text-right numeric text-emerald-700">{fmtCur(s.paid_total, s.currency)}</td>
                    <td className="px-4 py-2 text-right numeric" style={{ color: s.outstanding > 0 ? "#E0000F" : "#52525B" }}>{fmtCur(s.outstanding, s.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="p-3 border border-zinc-200 rounded bg-white">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-mono-data">
        <Icon size={13} style={{ color }} weight="duotone"/> {label}
      </div>
      <div className="font-heading text-xl font-extrabold mt-0.5 numeric" style={{ color }}>{value}</div>
    </div>
  );
}
